import React from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import Tooltip from "antd/lib/tooltip";
import { Consumer as IConsumer } from "Models/consumer";
import { PartitionType } from "Models/partition";
import { getLog, getPartitions } from "Redux/selectors";
import { SML_COLORS, CONSUMER_MESSAGES_LIMIT } from "Components/constants";
import Line from "./Line";
import Log from "./Log";
import Indicator from "./Indicator";
import Head from "./Head";
import Progress from "./Progress";
import styles from "./Consumer.module.css";

type ConsumerProps = {
    consumer: IConsumer;
};

const Consumer = ({ consumer }: ConsumerProps) => {
    const log = useSelector((state) => getLog(state, consumer.logUid));
    const getOffsetMessageIndex = React.useCallback(
        (messageId: number) => log.findIndex((m) => m.id === messageId),
        [log]
    );

    const partitions = useSelector(getPartitions);
    const partitionOffsets = React.useMemo(
        () =>
            partitions
                .filter((p) => p.type === PartitionType.LEAD)
                .filter((p) => consumer.leads.includes(p.id))
                .map(
                    (p) => [p.id, p.offset[consumer.group]] as [number, number]
                ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [partitions]
    );

    return (
        <Tooltip title="Consumer" placement="topLeft">
            <div className={clsx(styles.root)}>
                <Line
                    direction="horizontal"
                    length={(CONSUMER_MESSAGES_LIMIT + 0.5) * 60}
                    className={styles.line}
                />
                <Head
                    text={`${consumer.id}`}
                    color={consumer.leads.map(
                        (lead) => SML_COLORS[lead % SML_COLORS.length]
                    )}
                    replica={consumer.leads.length === 0}
                />
                <Progress
                    duration={consumer.interval - 0.5}
                    delay={1}
                    processedId={log?.[log.length - 1]?.id}
                />
                <Log
                    logUid={consumer.logUid}
                    delay={0.75}
                    limit={CONSUMER_MESSAGES_LIMIT}
                >
                    {partitionOffsets.map(
                        ([partitionId, offset]) =>
                            offset > 0 && (
                                <Indicator
                                    key={partitionId}
                                    position={getOffsetMessageIndex(offset)}
                                    color={
                                        SML_COLORS[
                                            partitionId % SML_COLORS.length
                                        ]
                                    }
                                    delay={1}
                                    text={`${partitionId}`}
                                    inverse
                                    offset={offset}
                                    partition={partitionId}
                                    group={consumer.group}
                                    type="source"
                                />
                            )
                    )}
                </Log>
            </div>
        </Tooltip>
    );
};

export default Consumer;
